import { SectionHeader } from "@/components/ui/SectionHeader";
import { StepCard } from "@/components/ui/StepCard";
import { landingContent } from "@/content/landing";

interface HowItWorksProps {
  className?: string;
  wrapOnTablet?: boolean;
}

export function HowItWorks({ className = "", wrapOnTablet = false }: HowItWorksProps) {
  const { steps, title, subtitle } = landingContent.how;

  return (
    <section
      id="how-it-works"
      className={`relative flex flex-col items-center gap-[40px] py-[80px] overflow-hidden ${className}`}
    >
      {/* Soft airy brand glow centred horizontally and vertically: a single radial ellipse fading to
          fully transparent toward the left/right edges (white at the section margins) and toward the
          top/bottom edges, so it reads as a soft centred wash rather than a full-width plate. Almost no
          tint sits directly behind the heading. No hard seam, no flat fill. Identical on every breakpoint. */}
      <div
        className="absolute inset-0 pointer-events-none max-tablet:hidden"
        style={{
          background:
            "radial-gradient(ellipse 62% 56% at 50% 50%, var(--brand-primary-soft) 0%, rgba(255,255,255,0) 72%)",
        }}
      />
      {/* mobile (<769): stacked layout stretches the section tall — tile a centred blob every
          fixed px so coverage stays even at any height; edge-masked so no seam / no header tint. */}
      <div
        className="absolute inset-0 pointer-events-none hidden max-tablet:block"
        style={{
          background:
            "color-mix(in srgb, var(--brand-primary-soft) 50%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0px, #000 170px, #000 calc(100% - 120px), transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0px, #000 170px, #000 calc(100% - 120px), transparent 100%)",
        }}
      />

      <SectionHeader className="relative z-[1]"
        title={title}
        subtitle={subtitle}
      />

      {/* Desktop ≥1269: 3 cards in a row */}
      <div className="relative flex gap-[16px] w-[1248px] max-desktop:hidden">
        {steps.map((s) => (
          <StepCard key={s.step} step={s.step} title={s.title} description={s.description} className={s.className}>
            {s.illustration}
          </StepCard>
        ))}
      </div>

      {/* Tablet 769–1268: horizontal scroll carousel (same pattern as TestimonialsV2).
          Mobile <769: vertical stack, full-width cards, no horizontal scroll. */}
      <div
        className={`no-scrollbar relative z-[1] hidden w-full gap-[16px] overflow-x-auto px-[20px] max-desktop:flex max-tablet:flex-col max-tablet:overflow-visible ${wrapOnTablet ? "max-desktop:flex-wrap max-desktop:justify-center max-desktop:!overflow-visible" : ""}`}
        style={{
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {steps.map((s) => (
          <StepCard
            key={s.step}
            step={s.step}
            title={s.title}
            description={s.description}
            className={`${s.className ?? ""} ${wrapOnTablet ? "max-desktop:!w-[calc(50%_-_8px)] max-tablet:!w-full max-tablet:!shrink" : ""}`}
          >
            {s.illustration}
          </StepCard>
        ))}
      </div>
    </section>
  );
}
